import { useContext } from "react";
import { Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { PageLoading } from "components/PageLoading";
import { PERMISSIONS } from "constants/portal";
import { getEnv } from "helpers/environment";
import { useIsOwner } from "hooks/useIsOwner";

export const determineAccess = (permissions, permissionName, userRoleId) => {
  return permissions
    ?.find((permission) => permission.name === permissionName)
    ?.roleId?.includes(userRoleId);
};

export const determineUserRoleId = (users, userId) => {
  if (users && userId) {
    return users.find((user) => user.auth0_id === userId)?.role_id;
  }
};

const routesThatHasLoadingFallback = ["/home"];

const isActive = (component) => {
  return component === "active";
};

export default function ProtectedRoute({ children, route }) {
  const { isLoading } = useAuth0();
  const { userPermissions } = useContext(PermissionsContext);
  // const { customerSubscription, isCustomerSubscriptionDoneFetching } = useSelector(
  //   (store) => store?.portal?.subscriptions
  // );
  const isOwner = useIsOwner();

  const getPermission = (permissionName) => {
    return userPermissions?.find((permission) => permission.name === permissionName)?.value;
  };

  if (isLoading || !userPermissions) {
    if (routesThatHasLoadingFallback.includes(route)) return <PageLoading />;
    return <></>;
  }

  const hasAccess = () => {
    if (isOwner) return true;

    // if (
    //   !customerSubscription &&
    //   route !== "/administration/subscriptions" &&
    //   route !== "/administration/billing"
    // ) {
    //   return false;
    // }

    switch (route) {
      case "/home":
        return true;
      case "/administration/company":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_COMPANY")) &&
          getPermission(PERMISSIONS.ACCESS_TO_COMPANY_PAGE)
        );
      case "/administration/billing":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_BILLING")) &&
          getPermission(PERMISSIONS.ACCESS_TO_BILLING_PAGE)
        );
      case "/administration/users":
      case "/account/users/profile-overview":
      case "/account/users/profile-overview/:id":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_USERS")) &&
          getPermission(PERMISSIONS.ACCESS_TO_USERS_PAGE)
        );
      case "/account/users/user-edit/:id":
        return isActive(getEnv("IS_COMPONENT_DOWN_USERS")) && getPermission(PERMISSIONS.EDIT_USERS);
      // case "/administration/subscriptions":
      //   return (
      //     isActive(getEnv("IS_COMPONENT_DOWN_SUBS")) &&
      //     getPermission(PERMISSIONS.ACCESS_TO_SUBSCRIPTIONS_PAGE)
      //   );
      case "/administration/roles-and-permissions":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_ROLES_PERMISSION")) &&
          getPermission(PERMISSIONS.ACCESS_TO_ROLES_AND_PERMISSIONS_PAGE)
        );
      case "/administration/credential-management":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_CREDENTIAL_MANAGEMENT")) &&
          getPermission(PERMISSIONS.ACCESS_TO_CREDENTIAL_MANAGEMENT_PAGE)
        );
      case "/designer":
      case "/designer/:id":
      case "/designer/new":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_WORKFLOW_DESIGNER")) &&
          getPermission(PERMISSIONS.ACCESS_TO_DESIGNER)
        );
      case "/library/flows":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_WORKFLOW_MGMT")) &&
          getPermission(PERMISSIONS.ACCESS_TO_WORKFLOWS_PAGE)
        );
      case "/home/profile":
      case "/privacy-and-security":
      case "/home/settings":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_HOME")) &&
          getPermission(PERMISSIONS.ACCESS_TO_ACCOUNT_SETTINGS)
        );
      // case "/customer-subscriptions":
      //   return getPermission(PERMISSIONS.ACCESS_TO_CUSTOMER_SUBSCRIPTIONS_PAGE);
      case "/administration/account":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_ACCOUNT")) &&
          getPermission(PERMISSIONS.ACCESS_TO_ACCOUNT_PAGE)
        );
      case "/quick-start":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_QUICK_START")) &&
          getPermission(PERMISSIONS.ACCESS_TO_QUICK_START_PAGE)
        );
      case "/analytics/workflows":
        return isActive(getEnv("IS_COMPONENT_DOWN_ANALYTICS_WORK_FLOWS"));
      case "/build/integrations":
        return isActive(getEnv("IS_COMPONENT_DOWN_BUILD_INTEGRATIONS"));
      case "/analytics/integrations":
        return isActive(getEnv("IS_COMPONENT_DOWN_ANALYTICS_INTEGRATIONS"));
      case "/analytics/logs":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_ANALYTICS_LOGS")) &&
          getPermission(PERMISSIONS.ACCESS_TO_LOGS_PAGE)
        );
       case "/workspace/logs":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_LOGS") ?? getEnv("IS_COMPONENT_DOWN_ANALYTICS_LOGS")) &&
          getPermission(PERMISSIONS.ACCESS_TO_LOGS_PAGE)
        );
      case "/resources/marketplace":
        return isActive(getEnv("IS_COMPONENT_DOWN_MARKET_PLACE"));
      case "/help/support":
        return isActive(getEnv("IS_COMPONENT_DOWN_SUPPORT"));
      case "/help/contact":
        return isActive(getEnv("IS_COMPONENT_DOWN_CONTACT"));
      case "/library/connectors":
        return (
          isActive(getEnv("IS_COMPONENT_DOWN_BUILD_CONNECTORS")) &&
          getPermission(PERMISSIONS.ACCESS_TO_CONNECTORS_PAGE)
        );
      
      case "/library/schedules":
        return getPermission(PERMISSIONS.ACCESS_TO_SCHEDULER_PAGE);
      default:
        return true;
    }
  };

  return hasAccess() ? children : <Navigate to="/home" />;
}
